import React from "react";
import { Box, Typography, Avatar } from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export type HeaderDialogVariant = "mobile" | "desktop";
export type HeaderDialogSeverity = "success" | "warning" | "error" | string;

type Props = {
  title: string;
  subtitle?: string;
  severity: HeaderDialogSeverity;
  variant?: HeaderDialogVariant;
};

const CONFIG = {
  success: {
    icon: CheckCircleOutlineIcon,
    color: "success.main",
    bgColor: "success.light",
  },
  warning: {
    icon: WarningAmberIcon,
    color: "warning.main",
    bgColor: "background.paper",
  },
  error: {
    icon: ErrorOutlineIcon,
    color: "error.main",
    bgColor: "error.light",
  },
} as const;

export function HeaderDialog({
  title,
  subtitle,
  severity,
  variant = "desktop",
}: Props) {
  const isMobile = variant === "mobile";
  const { icon: Icon, color, bgColor } = CONFIG[severity];

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      px={isMobile ? 1 : 0}
      mb={isMobile ? 2.5 : 3}
    >
      <Avatar
        sx={{
          bgcolor: bgColor,
          color,
          width: isMobile ? 50 : 64,
          height: isMobile ? 50 : 64,
          mb: 1.25,
          opacity: 0.9,
        }}
      >
        <Icon sx={{ fontSize: isMobile ? 28 : 34 }} />
      </Avatar>

      <Typography
        variant={isMobile ? "h6" : "h5"}
        fontWeight={900}
        color={color}
        sx={{ lineHeight: 1.15 }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
            maxWidth: isMobile ? "100%" : 520,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
